/**
 * Authenticated fetch against our own /api. Attaches the Entra access token
 * (or dev-bypass headers when VITE_AUTH_DEV_BYPASS=1) and normalizes errors.
 *
 * Also tracks each cloud-linked project's last-seen `rev` (the server's
 * optimistic-concurrency counter). A project becomes cloud-linked by a manual
 * Save to Cloud or an Open from Cloud; background autosync only runs for
 * linked projects and sends the tracked rev so a save from another session is
 * detected (409) instead of overwritten.
 */

import { authDevBypass, getApiToken } from './msal';
import type { DesignerProject } from '../designer/model/project';

export interface CloudProjectSummary {
  id: string;
  name: string;
  updatedAt: string;
  rev: number;
  shared: boolean;
}

export interface SharedProjectSummary {
  id: string;
  name: string;
  updatedAt: string;
  ownerOid: string;
  ownerName: string;
}

export interface CloudLibrary {
  mine: CloudProjectSummary[];
  shared: SharedProjectSummary[];
}

export interface CloudTemplateSummary {
  id: string;
  name: string;
  updatedAt: string;
  shared: boolean;
}

export interface SharedTemplateSummary {
  id: string;
  name: string;
  updatedAt: string;
  ownerOid: string;
  ownerName: string;
}

export interface CloudTemplateLibrary {
  mine: CloudTemplateSummary[];
  shared: SharedTemplateSummary[];
}

export interface CloudRevisionSummary {
  rev: number;
  name: string;
  savedAt: string;
}

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string) {
    super(`api ${status}: ${code}`);
    this.status = status;
    this.code = code;
  }
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (authDevBypass) {
    headers.set('x-dev-user', 'dev-user');
  } else {
    headers.set('Authorization', `Bearer ${await getApiToken()}`);
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let code = res.statusText;
    try {
      code = (await res.json()).error ?? code;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code);
  }
  return res;
}

// --- Cloud-link bookkeeping (session-scoped) --------------------------------

const cloudRevs = new Map<string, number>();

/** Last-seen server rev for a project, or undefined when not cloud-linked. */
export function getCloudRev(projectId: string): number | undefined {
  return cloudRevs.get(projectId);
}

export function unlinkCloudProject(projectId: string): void {
  cloudRevs.delete(projectId);
}

// --- API calls --------------------------------------------------------------

export async function listCloudProjects(): Promise<CloudLibrary> {
  return (await apiFetch('/api/projects')).json();
}

export async function loadCloudProject(
  id: string,
  ownerOid?: string
): Promise<{ doc: DesignerProject; rev: number; updatedAt: string }> {
  const qs = ownerOid ? `?owner=${encodeURIComponent(ownerOid)}` : '';
  const result = await (await apiFetch(`/api/projects/${encodeURIComponent(id)}${qs}`)).json();
  // Foreign (shared) projects are opened as copies — never rev-linked.
  if (!ownerOid) cloudRevs.set(id, result.rev);
  return result;
}

/**
 * Manual save (or restore): creates or overwrites unconditionally, links the
 * project, and always records a timeline checkpoint.
 */
export async function saveCloudProject(doc: DesignerProject): Promise<void> {
  const res = await apiFetch(`/api/projects/${encodeURIComponent(doc.meta.id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc, checkpoint: true }),
  });
  const { rev } = await res.json();
  cloudRevs.set(doc.meta.id, rev);
}

export type AutoSyncResult =
  | 'saved'
  | 'skipped'
  | 'conflict'
  | 'cloud-exists'
  | 'gone'
  | 'error';

/**
 * Background push of a project. Linked projects send the last-seen rev — the
 * server refuses (409) when another session saved in between, and the caller
 * surfaces that instead of overwriting.
 *
 * Unlinked projects: genuinely NEW projects (no cloud row) are created on
 * their first autosave — everything lives in the cloud by default. But when a
 * cloud copy already exists (e.g. crash-recovery restored a STALE local copy
 * on another machine), autosync must never blind-overwrite it: it reports
 * 'cloud-exists' and pushes nothing until the user chooses (Open from Cloud
 * to take the cloud copy, or manual Save to Cloud to overwrite).
 */
export async function autoSyncCloudProject(doc: DesignerProject): Promise<AutoSyncResult> {
  const id = doc.meta.id;
  const baseRev = cloudRevs.get(id);
  try {
    if (baseRev === undefined) {
      try {
        await apiFetch(`/api/projects/${encodeURIComponent(id)}`, { method: 'GET' });
        return 'cloud-exists'; // a copy exists that this session never saw
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 404)) throw err;
        // 404 → genuinely new: fall through and create.
      }
    }
    const res = await apiFetch(`/api/projects/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(baseRev === undefined ? { doc } : { doc, baseRev }),
    });
    const { rev } = await res.json();
    cloudRevs.set(id, rev);
    return 'saved';
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) return 'conflict';
    if (err instanceof ApiError && err.status === 404) {
      // Deleted from another session — stop autosyncing it.
      cloudRevs.delete(id);
      return 'gone';
    }
    return 'error'; // offline or transient — next edit retries
  }
}

export async function setCloudShared(id: string, shared: boolean): Promise<void> {
  await apiFetch(`/api/projects/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared }),
  });
}

export async function listCloudRevisions(id: string): Promise<CloudRevisionSummary[]> {
  return (await apiFetch(`/api/projects/${encodeURIComponent(id)}/revisions`)).json();
}

export async function loadCloudRevision(
  id: string,
  rev: number
): Promise<{ doc: DesignerProject; rev: number; savedAt: string }> {
  return (await apiFetch(`/api/projects/${encodeURIComponent(id)}/revisions/${rev}`)).json();
}

export async function deleteCloudProject(id: string): Promise<void> {
  await apiFetch(`/api/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  cloudRevs.delete(id);
}

// --- Cloud office templates -------------------------------------------------

export async function listCloudTemplates(): Promise<CloudTemplateLibrary> {
  return (await apiFetch('/api/templates')).json();
}

/** Fetch a template doc. Pass ownerOid to read another user's shared one. */
export async function loadCloudTemplate(
  id: string,
  ownerOid?: string
): Promise<{ doc: DesignerProject; updatedAt: string }> {
  const qs = ownerOid ? `?owner=${encodeURIComponent(ownerOid)}` : '';
  return (await apiFetch(`/api/templates/${encodeURIComponent(id)}${qs}`)).json();
}

/** Upsert one of the caller's templates; the name comes from doc.meta.name. */
export async function saveCloudTemplate(id: string, doc: DesignerProject): Promise<void> {
  await apiFetch(`/api/templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc }),
  });
}

export async function setCloudTemplateShared(id: string, shared: boolean): Promise<void> {
  await apiFetch(`/api/templates/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared }),
  });
}

export async function deleteCloudTemplate(id: string): Promise<void> {
  await apiFetch(`/api/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Presence (soft locking) ------------------------------------------------
//
// "Who else has this cloud project open" — advisory only, so every helper is
// deliberately non-throwing: an old server (404/501 on these routes), an
// expired session, or being offline must never break opening or editing.
// Failures degrade to "nobody else here".

export interface PresenceViewer {
  oid: string;
  name: string;
  /** Milliseconds since this viewer's last heartbeat. */
  lastSeenMs: number;
}

/**
 * Record that the caller has this project open. Pass ownerOid when the open
 * project belongs to someone else. Never throws.
 */
export async function sendPresenceHeartbeat(projectId: string, ownerOid?: string): Promise<void> {
  try {
    await apiFetch('/api/presence/heartbeat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ownerOid ? { projectId, ownerOid } : { projectId }),
    });
  } catch {
    // Advisory signal only — old server, offline, expired session all fine.
  }
}

/**
 * Other people currently viewing a project ("me" = the caller's own project).
 * Never throws — failure reads as an empty room.
 */
export async function fetchPresence(
  projectId: string,
  ownerOid = 'me'
): Promise<PresenceViewer[]> {
  try {
    const res = await apiFetch(
      `/api/presence/${encodeURIComponent(ownerOid)}/${encodeURIComponent(projectId)}`
    );
    const body = (await res.json()) as { viewers?: PresenceViewer[] };
    return Array.isArray(body.viewers) ? body.viewers : [];
  } catch {
    return [];
  }
}

/**
 * Bulk presence over the caller's own projects (library decoration):
 * id → other viewers. Never throws — failure reads as an empty map.
 */
export async function fetchPresenceBulk(
  ids: string[]
): Promise<Record<string, PresenceViewer[]>> {
  if (ids.length === 0) return {};
  try {
    const res = await apiFetch(`/api/presence?ids=${encodeURIComponent(ids.join(','))}`);
    return (await res.json()) as Record<string, PresenceViewer[]>;
  } catch {
    return {};
  }
}

// --- Admin usage dashboard --------------------------------------------------

export interface AdminSummary {
  activeUsers7d: number;
  eventsByKind: Record<string, number>;
  topUsers: { userOid: string; userName: string; count: number }[];
  recentErrors: { at: string; userName: string; detail: Record<string, unknown> }[];
}

export interface AdminEvent {
  at: string;
  userOid: string;
  userName: string;
  sessionId: string;
  kind: string;
  detail: Record<string, unknown>;
}

/**
 * Whether the signed-in user is in ADMIN_USERS. Probes the admin whoami
 * endpoint once; any failure (403, offline, auth off) simply means false —
 * the server re-checks on every admin call, so this only drives visibility.
 */
export async function checkIsAdmin(): Promise<boolean> {
  try {
    const { admin } = await (await apiFetch('/api/admin/events/whoami')).json();
    return admin === true;
  } catch {
    return false;
  }
}

export async function fetchAdminSummary(): Promise<AdminSummary> {
  return (await apiFetch('/api/admin/events/summary')).json();
}

export async function fetchAdminEvents(opts: {
  user?: string;
  kind?: string;
  limit?: number;
} = {}): Promise<AdminEvent[]> {
  const params = new URLSearchParams();
  if (opts.user) params.set('user', opts.user);
  if (opts.kind) params.set('kind', opts.kind);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return (await apiFetch(`/api/admin/events${qs ? `?${qs}` : ''}`)).json();
}
